/**
 * An activity's colour and the company's packs (#202, option F of activities.prototype.html).
 *
 * ColorPick: the seven tones of the design, the pressed one in effect; blue is the tone of an activity with
 * none. Pakker: a pack is a name and a list of activities with a length each, placed one after another on a
 * shift from where it is put («Hele vakten» for 0 minutes). It holds names, so a pack keeps working when an
 * activity is renamed; the shift's save finds or makes each name in the library as it does any activity.
 * Every change saves at once (PUT /admin/activity-packs/:id), as the rows of the library above do.
 */
import {
  type ApiActivityPack,
  useActivityPackWrites,
  useActivityPacks,
  useShiftTaskLibrary,
} from '@/api/data';
import { T } from '@/i18n';
import { COLORS, colorOf } from '@/pages/vaktplan/activityIcon';
import { Button, Empty, Panel, toast } from '@/ui';
import { Icon } from '@/ui/icons';
import { useState } from 'react';
import s from './Pakker.module.css';

const COLOR_NAMES: Record<string, string> = {
  blue: 'Blå',
  teal: 'Blågrønn',
  violet: 'Fiolett',
  plum: 'Plomme',
  cyan: 'Turkis',
  slate: 'Skifer',
  indigo: 'Indigo',
};

export function ColorPick({
  task,
  color,
  disabled,
  onPick,
}: {
  task: string;
  color: string | null | undefined;
  disabled?: boolean;
  onPick: (c: string | null) => void;
}) {
  const now = colorOf(color);
  // read only: the colour in effect, nothing to press
  if (disabled)
    return (
      <span
        className={s.dot}
        style={{ display: 'inline-block', background: `var(--${now}-f)`, borderColor: `var(--${now}-l)` }}
        title={T(COLOR_NAMES[now] ?? now)}
      />
    );
  return (
    // biome-ignore lint/a11y/useSemanticElements: a row of toggle buttons, not a form's fieldset
    <div className={s.sw} role="group" aria-label={T('Farge på {task}', { task })}>
      {COLORS.map((c) => (
        <button
          type="button"
          key={c}
          className={s.dot}
          style={{ background: `var(--${c}-f)`, borderColor: `var(--${c}-l)` }}
          aria-pressed={now === c}
          aria-label={T(COLOR_NAMES[c] ?? c)}
          title={T(COLOR_NAMES[c] ?? c)}
          disabled={disabled}
          // blue is no choice at all: the activity has the default tone again
          onClick={() => now !== c && onPick(c === 'blue' ? null : c)}
        />
      ))}
    </div>
  );
}

export function Pakker({ mayEdit }: { mayEdit: boolean }) {
  const q = useActivityPacks();
  const lib = useShiftTaskLibrary();
  const w = useActivityPackWrites();
  const [problem, setProblem] = useState<string | null>(null);
  // a row without its list of activities is not a pack (an older server, a stray answer)
  const packs = (Array.isArray(q.data) ? q.data : []).filter((p) => Array.isArray(p.items));
  const names = (Array.isArray(lib.data) ? lib.data : []).filter((t) => t.is_active).map((t) => t.name);

  const run = async (f: () => Promise<unknown>) => {
    setProblem(null);
    try {
      await f();
    } catch (e) {
      setProblem(e instanceof Error ? e.message : T('Kunne ikke lagre.'));
    }
  };
  const save = (p: ApiActivityPack, body: Partial<Omit<ApiActivityPack, 'id'>>) =>
    run(() => w.update.mutateAsync({ id: p.id, body }));

  return (
    <Panel
      title={T('Pakker')}
      sub={T('aktiviteter som legges på en vakt sammen, etter hverandre')}
      actions={
        mayEdit ? (
          <Button
            small
            onClick={() =>
              run(async () => {
                await w.create.mutateAsync({ name: T('Ny pakke {n}', { n: packs.length + 1 }), items: [] });
                toast(T('Pakke opprettet'));
              })
            }
          >
            <Icon name="plus" />
            {T('Ny pakke')}
          </Button>
        ) : null
      }
      flush
    >
      {packs.length ? (
        <div className={s.list} data-testid="pack-list">
          {packs.map((p) => (
            <div className={s.pack} key={p.id} data-testid="pack">
              <div className={s.head}>
                <input
                  className="inp"
                  aria-label={T('Navn på pakken {name}', { name: p.name })}
                  key={p.name}
                  defaultValue={p.name}
                  disabled={!mayEdit}
                  onBlur={(e) => {
                    const name = e.target.value.trim();
                    if (name && name !== p.name) save(p, { name });
                  }}
                />
                <ColorPick
                  task={p.name}
                  color={p.color}
                  disabled={!mayEdit}
                  onPick={(color) => save(p, { color })}
                />
                <span className={s.sp} />
                {mayEdit && (
                  <Button
                    small
                    ghost
                    tone="dgr"
                    aria-label={T('Slett pakken {name}', { name: p.name })}
                    onClick={() => run(() => w.remove.mutateAsync(p.id))}
                  >
                    {T('Slett')}
                  </Button>
                )}
              </div>
              {p.items.map((it, k) => (
                <div className={s.item} key={it.name}>
                  <span className={s.n}>{it.name}</span>
                  <label className={s.len}>
                    <input
                      className="inp"
                      type="number"
                      min={0}
                      max={720}
                      step={15}
                      aria-label={T('Minutter for {task}', { task: it.name })}
                      key={`${it.name}-${it.minutes}`}
                      defaultValue={it.minutes}
                      disabled={!mayEdit}
                      onBlur={(e) => {
                        const minutes = Math.max(0, Math.min(720, Math.round(Number(e.target.value) || 0)));
                        if (minutes !== it.minutes)
                          save(p, { items: p.items.map((x, j) => (j === k ? { ...x, minutes } : x)) });
                      }}
                    />
                    <span>{it.minutes ? T('min') : T('Hele vakten')}</span>
                  </label>
                  {mayEdit && (
                    <button
                      type="button"
                      className={s.x}
                      aria-label={T('Fjern {task} fra {name}', { task: it.name, name: p.name })}
                      onClick={() => save(p, { items: p.items.filter((_, j) => j !== k) })}
                    >
                      <Icon name="close" />
                    </button>
                  )}
                </div>
              ))}
              {mayEdit && (
                <select
                  className={`inp ${s.add}`}
                  aria-label={T('Legg til en aktivitet i {name}', { name: p.name })}
                  value=""
                  onChange={(e) => {
                    const name = e.target.value;
                    if (name) save(p, { items: [...p.items, { name, minutes: 30 }] });
                  }}
                >
                  <option value="">{T('Legg til en aktivitet …')}</option>
                  {names
                    .filter((n) => !p.items.some((x) => x.name.toLowerCase() === n.toLowerCase()))
                    .map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                </select>
              )}
            </div>
          ))}
        </div>
      ) : (
        <Empty>{T('Ingen pakker ennå.')}</Empty>
      )}
      {problem ? (
        <p className="hint" data-testid="pack-problem" style={{ padding: '0 12px' }}>
          {problem}
        </p>
      ) : null}
    </Panel>
  );
}
